import { computed, inject, Injectable } from "@angular/core";
import {
  getStrategies,
  Marketplaces,
  OrganizationAccountGroups,
  SegmentEx,
  singleStrategyInStrategyGroup,
  StrategyEx,
  StrategyGroupEx,
} from "@front/m19-models";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  AccountSelectionService,
  AudiencesService,
  AuthService,
  BaseStrategyService,
  ELIGIBLE_AUDIENCE_BOOST_CAMPAIGN_TYPES,
  isAmazonBusinessAvailable,
  OrganizationAccountGroupService,
  SegmentService,
  SpStrategyService,
} from "@front/m19-services";
import { AdvancedSettings } from "@m19-board/strategies/strategy-page/strategy-page-advanced-settings/strategy-page-advanced-settings.component";
import { AccountMarketplace, AlgoMode, CampaignType, StrategyType, TacticType, User } from "@front/m19-api-client";
import { map, Observable, startWith, Subject, switchMap } from "rxjs";
import { ActivatedRoute } from "@angular/router";
import { StrategyPageData } from "@m19-board/strategies/strategy-page/strategy-page.resolver";
import { StrategyService } from "@front/m19-injection-token";
import { StrategyFromCT } from "@front/m19-models/strategy/StrategyBuilder";

@Injectable()
export class StrategyPageService<T extends CampaignType> {
  private readonly organizationAccountGroupService = inject(OrganizationAccountGroupService);
  private readonly accountMarketplaceSelection = inject(AccountSelectionService);
  private readonly segmentService = inject(SegmentService);
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly strategyService = inject<BaseStrategyService<T>>(StrategyService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly audiencesService = inject(AudiencesService);
  private readonly authService = inject(AuthService);

  private strategyData$ = this.activatedRoute.data.pipe(
    map((data) => {
      return (data as { strategy: StrategyPageData }).strategy;
    }),
  );

  accountMarketplace = toSignal(this.accountMarketplaceSelection.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  segmentIndex = toSignal<Map<number, SegmentEx>>(
    this.accountMarketplaceSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
      startWith(new Map()),
    ),
    { requireSync: true },
  );

  private strategyId = new Subject<{ strategyId: number; strategyGroupId: number | undefined }>();
  private user = toSignal(this.authService.loggedUser$);
  private strategyAndStrategyGroup = toSignal(
    this.strategyData$.pipe(
      switchMap((strategyData) => this.getStrategy(strategyData.strategyId, strategyData.strategyGroupId)),
    ),
  );
  strategy = computed(() => this.strategyAndStrategyGroup()?.strategy);
  strategyGroup = computed(() => this.strategyAndStrategyGroup()?.strategyGroup);

  allOrganizationAccountGroups = toSignal(this.organizationAccountGroupService.allOrganizationAccountGroups$);

  isSingleStrategyInStrategyGroup = computed(
    () => this.strategyGroup() && singleStrategyInStrategyGroup(this.strategyGroup()!),
  );

  advancedSettings = computed<Record<AdvancedSettings, boolean>>(() => {
    return StrategyPageService.getAdvancedSettings(
      this.strategy()!,
      this.accountMarketplace(),
      this.allOrganizationAccountGroups()!,
      this.strategyGroup(),
      this.user(),
    );
  });

  setStrategyParams(strategyId: number, strategyGroupId: number | undefined = undefined) {
    this.strategyId.next({ strategyId, strategyGroupId });
  }

  getStrategy(
    strategyId: number,
    strategyGroupId: number | undefined,
  ): Observable<{
    strategy: StrategyFromCT<T>;
    strategyGroup?: StrategyGroupEx;
  }> {
    return this.accountMarketplaceSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => {
        if (strategyGroupId) {
          return this.spStrategyService.getStrategyGroups(am.accountId, am.marketplace).pipe(
            map((strategyGroups) => strategyGroups.get(strategyGroupId)),
            map((strategyGroup) => {
              const strategies = getStrategies(strategyGroup!);
              return {
                strategy: strategies.find((s) => s.strategyId == strategyId)! as StrategyFromCT<T>,
                strategyGroup: strategyGroup,
              };
            }),
          );
        } else {
          return this.strategyService.getStrategies(am.accountId, am.marketplace).pipe(
            map((strategies) => ({
              strategy: strategies.get(strategyId)!,
              strategyGroup: undefined,
            })),
          );
        }
      }),
    );
  }

  static getAdvancedSettings(
    strategy: StrategyEx,
    accountMarketplace: AccountMarketplace,
    organizationAccountGroups: OrganizationAccountGroups[],
    strategyGroup?: StrategyGroupEx,
    user?: User,
  ): Record<AdvancedSettings, boolean> {
    const onlyB2B = strategy.isSpStrategy() && strategy.onlyB2B;
    return {
      strategyLabel: !!user?.allowCustomCampaignName,
      targetMinDailySpend: !!strategy && strategy.algoMode === AlgoMode.ACOS_TARGET,
      autoCampaign:
        (strategy.campaignType === CampaignType.SP &&
          (this.checkIsProductStrategyType(strategy) || this.isLegacyStrategyType(strategy))) ||
        strategy.campaignType === CampaignType.SB,
      productTargeting:
        (this.checkIsProductStrategyType(strategy) || this.isLegacyStrategyType(strategy)) &&
        strategy.campaignType !== CampaignType.SD &&
        strategy.campaignType !== CampaignType.DSP,
      aiPoweredTargeting: this.showAutoAlgoExploration(strategy),
      disableProductTargeting: false,
      dayparting:
        strategy.campaignType === CampaignType.SP &&
        Marketplaces[accountMarketplace.marketplace].hourlyDataSupport &&
        this.checkHasAccessToHourlyData(accountMarketplace, organizationAccountGroups),
      tosro:
        strategy.campaignType === CampaignType.SP &&
        (strategy.strategyType === StrategyType.BRAND || strategy.strategyType === StrategyType.KEYWORD),
      strategyGroup: !!strategyGroup && this.checkIsSingleStrategyInStrategyGroup(strategyGroup),
      boostAudience: !!strategy && !onlyB2B && ELIGIBLE_AUDIENCE_BOOST_CAMPAIGN_TYPES.has(strategy.campaignType),
      frequencyCap: strategy.campaignType === CampaignType.DSP,
      boostAmazonBusiness:
        !onlyB2B &&
        strategy.campaignType === CampaignType.SP &&
        isAmazonBusinessAvailable(accountMarketplace.marketplace),
      onlyB2B,
      agencyFee: strategy.campaignType === CampaignType.DSP,
      boostPlacementTop:
        strategy.campaignType === CampaignType.SP &&
        (strategy.strategyType === StrategyType.BRAND || strategy.strategyType === StrategyType.KEYWORD),
    };
  }

  private static checkHasAccessToHourlyData(
    accountMarketplace: AccountMarketplace,
    organizationAccountGroups: OrganizationAccountGroups[],
  ): boolean {
    return (
      (organizationAccountGroups ?? []).find((org) => org.id === accountMarketplace.resourceOrganizationId)!
        .organization!.billingPlan!.hourlyDataSupport === true
    );
  }

  private static checkIsProductStrategyType(strategy: StrategyEx): boolean {
    return strategy.strategyType === StrategyType.PRODUCT;
  }

  private static isLegacyStrategyType(strategy: StrategyEx): boolean {
    return strategy.isLegacy();
  }

  static checkIsSingleStrategyInStrategyGroup(strategyGroup: StrategyGroupEx): boolean {
    return !!strategyGroup && singleStrategyInStrategyGroup(strategyGroup);
  }

  private static showAutoAlgoExploration(strategy: StrategyEx): boolean {
    return (
      !!strategy &&
      strategy.isAdvertisingStrategy() &&
      strategy.campaignType !== CampaignType.SD && // AI-powered targeting is set via targetings section on Sponsored Display
      strategy.tactics.length != 0 &&
      (!strategy.autoAlgoExplorationEnabled || strategy.tactics.some((x) => x.tacticType != TacticType.BLACKLIST))
    );
  }
}
